import { useEffect, useState } from "react";
import { useApi } from "../useApi";
import { useProject } from "../projectContext";
import type { AgentDefinition, FoundationModel, InferenceProfile, Project } from "../types";

// Models that support the effort parameter (extended thinking).
const EFFORT_MODELS = new Set([
  "anthropic.claude-opus-4",
  "anthropic.claude-opus-4-7",
]);

function supportsEffort(modelId: string): boolean {
  const normalised = modelId.replace(/^us\./, "");
  return [...EFFORT_MODELS].some((prefix) => normalised.startsWith(prefix));
}

const FALLBACK_PROFILES: InferenceProfile[] = [
  { inferenceProfileId: "us.anthropic.claude-haiku-4-5-20251001-v1:0", inferenceProfileName: "US Anthropic Claude Haiku 4.5" },
  { inferenceProfileId: "us.anthropic.claude-sonnet-4-20250514-v1:0", inferenceProfileName: "US Anthropic Claude Sonnet 4" },
  { inferenceProfileId: "us.anthropic.claude-opus-4-20250514-v1:0", inferenceProfileName: "US Anthropic Claude Opus 4" },
];
const FALLBACK_MODELS: FoundationModel[] = [];

export default function AgentsPage() {
  const { project: summary } = useProject();
  const { call } = useApi();
  const [project, setProject] = useState<Project | undefined>();
  const [profiles, setProfiles] = useState<InferenceProfile[]>(FALLBACK_PROFILES);
  const [models, setModels] = useState<FoundationModel[]>(FALLBACK_MODELS);
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [message, setMessage] = useState<string | undefined>();
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    call<Project>(`/api/projects/${summary.projectId}`)
      .then(setProject)
      .catch((e: Error) => setError(e.message));
    call<InferenceProfile[]>("/api/bedrock/inference-profiles")
      .then((p) => { if (p.length > 0) setProfiles(p); })
      .catch(() => {});
    call<FoundationModel[]>("/api/bedrock/foundation-models")
      .then((m) => { if (m.length > 0) setModels(m); })
      .catch(() => {});
  }, [call, summary.projectId]);

  if (error) return <p className="error">{error}</p>;
  if (!project) return <p>Loading…</p>;

  const readOnly = summary.permission === "reader";
  const agents = project.agents ?? [];
  const agent = agents[selectedIdx] as AgentDefinition | undefined;

  const setAgent = (idx: number, patch: Partial<AgentDefinition>) => {
    const next = [...agents];
    next[idx] = { ...next[idx], ...patch };
    setProject({ ...project, agents: next });
  };
  const addAgent = () => {
    const next = [
      ...agents,
      { name: "elementa_new", model: profiles[0]?.inferenceProfileId ?? "", prompt: "" },
    ];
    setProject({ ...project, agents: next });
    setSelectedIdx(next.length - 1);
  };
  const removeAgent = (idx: number) => {
    const next = [...agents];
    next.splice(idx, 1);
    setProject({ ...project, agents: next });
    setSelectedIdx(Math.min(selectedIdx, next.length - 1));
  };

  const save = async () => {
    setSaving(true);
    setMessage(undefined);
    setError(undefined);
    try {
      const updated = await call<Project>(`/api/projects/${summary.projectId}`, {
        method: "PUT",
        body: JSON.stringify(project),
      });
      setProject(updated);
      setMessage("Agents saved.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    setResetting(true);
    setMessage(undefined);
    setError(undefined);
    try {
      const updated = await call<Project>(`/api/projects/${summary.projectId}/reset-agents`, {
        method: "POST",
      });
      setProject(updated);
      setSelectedIdx(0);
      setMessage("Agents reset to defaults.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setResetting(false);
    }
  };

  const isKnownProfile = agent ? profiles.some((p) => p.inferenceProfileId === agent.model) : false;
  const isKnownModel = agent ? models.some((m) => m.modelId === agent.model) : false;
  const isKnown = isKnownProfile || isKnownModel;
  const effortSupported = agent ? supportsEffort(agent.model) : false;

  return (
    <div className="page-fill">
      <div className="page-header">
        <h1>Agents</h1>
      </div>

      <div className="agent-selector">
        <select
          value={selectedIdx}
          onChange={(e) => setSelectedIdx(Number(e.target.value))}
        >
          {agents.map((a, i) => (
            <option key={i} value={i}>{a.name}{a.description ? ` — ${a.description}` : ""}</option>
          ))}
        </select>
        {!readOnly && (
          <>
            <button className="secondary" onClick={addAgent}>+ Add</button>
            {agent && <button className="secondary danger" onClick={() => removeAgent(selectedIdx)}>Remove</button>}
            <button className="secondary" disabled={resetting} onClick={resetToDefaults}>{resetting ? "Resetting…" : "Reset Defaults"}</button>
          </>
        )}
        {!readOnly && (
          <button disabled={saving} onClick={save} style={{ marginLeft: "auto" }}>{saving ? "Saving…" : "Save"}</button>
        )}
        {message && <span className="msg">{message}</span>}
      </div>

      {agent && (
        <div className="agent-detail">
          <div className="agent-fields">
            <div className="field inline">
              <label>Name</label>
              <div className="flex">
                <span className="muted" style={{ whiteSpace: "nowrap" }}>elementa_</span>
                <input
                  value={agent.name.replace(/^elementa_/, "")}
                  disabled={readOnly}
                  onChange={(e) => setAgent(selectedIdx, { name: `elementa_${e.target.value.replace(/[^a-z0-9_]/g, "")}` })}
                />
              </div>
            </div>
            <div className="field inline">
              <label>Description</label>
              <input
                value={agent.description ?? ""}
                disabled={readOnly}
                onChange={(e) => setAgent(selectedIdx, { description: e.target.value })}
              />
            </div>
            <div className="field inline">
              <label>Model</label>
              <select
                value={isKnown ? agent.model : "__custom__"}
                disabled={readOnly}
                onChange={(e) => {
                  if (e.target.value === "__custom__") return;
                  setAgent(selectedIdx, { model: e.target.value });
                }}
              >
                <optgroup label="Anthropic (inference profiles)">
                  {profiles.map((p) => (
                    <option key={p.inferenceProfileId} value={p.inferenceProfileId}>{p.inferenceProfileName}</option>
                  ))}
                </optgroup>
                {models.length > 0 && (
                  <optgroup label="Other providers (foundation models)">
                    {models.map((m) => (
                      <option key={m.modelId} value={m.modelId}>{m.modelName} ({m.providerName})</option>
                    ))}
                  </optgroup>
                )}
                <option value="__custom__">Custom…</option>
              </select>
              <input
                style={{ marginTop: 4 }}
                value={agent.model}
                disabled={readOnly}
                onChange={(e) => setAgent(selectedIdx, { model: e.target.value })}
              />
            </div>
            {effortSupported && (
              <div className="field inline">
                <label>Effort</label>
                <select
                  value={agent.effort ?? "medium"}
                  disabled={readOnly}
                  onChange={(e) => setAgent(selectedIdx, { effort: e.target.value as AgentDefinition["effort"] })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="max">Max</option>
                </select>
              </div>
            )}
          </div>
          <div className="agent-prompt">
            <label>Prompt (Markdown)</label>
            <textarea
              value={agent.prompt}
              disabled={readOnly}
              onChange={(e) => setAgent(selectedIdx, { prompt: e.target.value })}
            />
          </div>
        </div>
      )}
    </div>
  );
}
